"use client";

import { useMemo } from "react";
import { KeyedMutator } from "swr";
import useLocalStorage from "use-local-storage";

import { useBaseServerAction } from "@/hooks/useServerAction";
import { BaseStoreProps, createAdvancedStore } from "@/stores/createAdvancedStore";
import { getKaspiCabinets } from "@/actions/kaspiCabinet";
import { IKaspiCabinet } from "@/models/KaspiCabinet";

import { useOrganizationStore } from "./organization.store";

//

export interface CurrentCabinetStore extends BaseStoreProps {
  cabinets: IKaspiCabinet[] | undefined;
  cabinet: IKaspiCabinet | null | undefined;
  setCabinet: (cabinet: IKaspiCabinet | null) => void;
  mutate: KeyedMutator<{
    totalCount: number;
    data: IKaspiCabinet[];
  }>;
  response:
    | {
        totalCount: number;
        data: IKaspiCabinet[];
      }
    | undefined;
}

export const currentCabinetStore = createAdvancedStore<CurrentCabinetStore>("Cabinet");
export const useCurrentCabinetContext = currentCabinetStore.useContext;

//

export interface CurrentCabinetProviderProps {
  children?: React.ReactNode;
}

export const CurrentCabinetProvider: React.FC<CurrentCabinetProviderProps> = ({ children }) => {
  const organization = useOrganizationStore();

  const { data, mutate } = useBaseServerAction(
    { key: "kaspi-cabinets", organizationId: organization.id },
    getKaspiCabinets,
  );

  const storaged = useLocalStorage<null | string>("currentCabinetId", null);
  const [currentCabinetId, setCurrentCabinetId] = storaged;

  const cabinets = data ? data.data : undefined;

  const cabinet = useMemo(() => {
    if (cabinets === undefined) return undefined;
    if (!currentCabinetId) return cabinets.at(0) ?? null;
    const currentCabinet = cabinets.find((c) => c._id.toString() === currentCabinetId);
    if (!currentCabinet) {
      setCurrentCabinetId(null);
      return cabinets.at(0) ?? null;
    }
    return currentCabinet;
  }, [cabinets, currentCabinetId, setCurrentCabinetId]);

  return (
    <currentCabinetStore.Provider
      value={(set) => ({
        cabinets,
        cabinet,
        setCabinet: (cabinet) => {
          set({ cabinet });
          setCurrentCabinetId(cabinet && cabinet._id.toString());
        },
        mutate,
        response: data,
      })}
    >
      {children}
    </currentCabinetStore.Provider>
  );
};
